import type {
  ChangeEvent,
  KeyboardEvent,
  ReactNode,
  InputHTMLAttributes,
  DOMAttributes,
  MouseEvent,
  FocusEvent,
} from "react";
import { useState, useRef, useEffect, useImperativeHandle, forwardRef } from "react";
import pluralize from "pluralize-ru";
import {
  Composite,
  VisuallyHidden,
  useCompositeState,
  // eslint-disable-next-line camelcase -- тип из reakit.
  unstable_useId,
  usePopoverState,
} from "reakit";
import flattenChildren from "react-keyed-flatten-children";
import invariant from "tiny-invariant";

import { Box } from "../Box/Box";
import { DisclosureField } from "./DisclosureField/DisclosureField";
import { Listbox } from "./Listbox/Listbox";
import { ListboxOption } from "./ListboxOption/ListboxOption";
import { ListboxContent } from "./ListboxContent/ListboxContent";
import { isComboboxChildrenEmpty, contentFilter } from "./helpers";
import type { FieldOptions } from "../Field/Field";

export { ListboxLoadingMessage as ComboboxLoadingMessage } from "./ListboxLoadingMessage/ListboxLoadingMessage";
export { ListboxEmptyMessage as ComboboxEmptyMessage } from "./ListboxEmptyMessage/ListboxEmptyMessage";
export { ListboxOption as ComboboxOption } from "./ListboxOption/ListboxOption";

/**
 * @beta
 */
export type ComboboxProps = {
  children: ReactNode[] | ReactNode;
  popoverAriaLabel: string;
  value: string;
  initialSelectedIndex?: number;
  onSelect: (value: string) => void;
  onChange: (value: string) => void;
} & FieldOptions &
  Omit<
    InputHTMLAttributes<HTMLInputElement>,
    "onChange" | "list" | "type" | "value" | keyof DOMAttributes<HTMLInputElement>
  >;

export const Combobox = forwardRef(
  (
    {
      children,
      popoverAriaLabel,
      value,
      initialSelectedIndex,
      onChange,
      onSelect,
      ...props
    }: ComboboxProps,
    ref
  ) => {
    const [moveCompositeOnShow, setMoveCompositeOnShow] = useState(true);
    const [selectedValue, setSelectedValue] = useState<string | null>(null);
    const disclosureFieldRef = useRef<HTMLInputElement>(null);
    const composite = useCompositeState({
      unstable_virtual: true,
      orientation: "vertical",
      loop: true,
    });

    const popoverState = usePopoverState({ gutter: 12 });
    const { id } = unstable_useId();

    /* Вообще, id генерируется в виде строки, но на всякий случай проверяем */
    invariant(typeof id === "string", "id должно быть строкой");
    const listboxId = `${id}-listbox`;
    const supportId = `${listboxId}-support`;

    const selectedItemId =
      composite.items.find(({ ref: itemRef }) => {
        let optionValue = itemRef.current?.getAttribute("value");
        return optionValue === selectedValue;
      })?.id ?? null;

    const isChildrenEmpty = isComboboxChildrenEmpty(children);
    const [mainContent, supportContent] = contentFilter(
      flattenChildren(children),
      ListboxOption.name
    );
    const isSupportContentEmpty = isComboboxChildrenEmpty(supportContent);
    function pluralizeContent(count: number): string {
      return pluralize(
        Math.abs(count),
        "%d вариантов",
        "%d вариант",
        "%d варианта",
        "%d вариантов"
      );
    }

    const matchText = isChildrenEmpty
      ? ""
      : `${popoverAriaLabel}, найдено ${pluralizeContent(mainContent.length)}`;

    const comboboxHint = isSupportContentEmpty ? matchText : supportContent;

    useImperativeHandle(ref, function provideAccessToComboboxField() {
      return {
        focus() {
          disclosureFieldRef.current?.focus();
        },
        get input() {
          return disclosureFieldRef.current;
        },
      };
    });

    useEffect(
      function setInitialSelectedId() {
        const isSelectedIndexValid =
          typeof initialSelectedIndex === "number" && initialSelectedIndex >= 0;

        if (isSelectedIndexValid && selectedValue === null && composite.items.length) {
          const targetValue =
            composite.items[initialSelectedIndex].ref.current?.getAttribute("value");

          setSelectedValue(targetValue ?? "");
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps -- другие зависимости не нужны
      [composite.items]
    );

    let { setCurrentId, move, first } = composite;

    useEffect(
      function moveToCurrentOptionOnListboxUpdate() {
        if (popoverState.visible && moveCompositeOnShow) {
          if (selectedItemId) {
            setCurrentId(selectedItemId);
            move(selectedItemId);
          } else {
            first();
          }
        }
        setMoveCompositeOnShow(true);
      },

      // eslint-disable-next-line react-hooks/exhaustive-deps -- другие зависимости не нужны
      [popoverState.visible, composite.items]
    );

    useEffect(
      function updatePopoverVisibility() {
        const isComboboxFieldFocused = disclosureFieldRef.current === document.activeElement;
        if (!isComboboxFieldFocused) return;
        // Если выбрали значение и появляется поповер со вспомогательным контентом, то показываем его
        if (!isSupportContentEmpty && selectedValue) {
          setTimeout(popoverState.show);
          return;
        }
        const shouldHidePopover = popoverState.visible && isChildrenEmpty;
        const shouldOpenPopover = !popoverState.visible && !isChildrenEmpty;
        if (shouldHidePopover || shouldOpenPopover) {
          popoverState.toggle();
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps -- другие зависимости не нужны
      [children, value]
    );

    const handleFieldClick = (event: MouseEvent<HTMLInputElement>) => {
      // Field это PopoverDisclosure, поэтому по клику открывается поповер
      // Отменяем показ поповера, если его содержимое пусто
      if (isChildrenEmpty) {
        event.preventDefault();
      }
    };

    const handleListboxFocus = (event: FocusEvent<HTMLElement>) => {
      disclosureFieldRef.current?.focus();
      event.preventDefault();
    };

    const handleFieldKeydown = (event: KeyboardEvent<HTMLInputElement>) => {
      const { code } = event;

      if (!disclosureFieldRef.current) return;

      // Так как Field это тоже Composite, необходимо иначе обрабатывать Space
      if (code === "Space") {
        const { selectionStart, selectionEnd } = disclosureFieldRef.current;

        if (selectionStart !== null && selectionEnd !== null) {
          disclosureFieldRef.current.setRangeText(` `, selectionStart, selectionEnd, "end");
        }
        const newValue = disclosureFieldRef.current.value;
        setSelectedValue("");
        onChange(newValue);
        event.preventDefault();
      }
      if (code === "Home") {
        disclosureFieldRef.current.setSelectionRange(0, 0);
      }
      if (code === "End") {
        const { length } = disclosureFieldRef.current.value;
        disclosureFieldRef.current.setSelectionRange(length, length);
      }
      if (!isChildrenEmpty) {
        if (code === "ArrowUp" && !popoverState.visible) {
          setMoveCompositeOnShow(false);
          popoverState.show();
          composite.last();
        }
        if (code === "ArrowDown" && !popoverState.visible) {
          popoverState.show();
        }
        if (code === "Escape" && popoverState.visible) {
          popoverState.hide();
        }
        // Field это Composite, поэтому событие FieldKeydown обрабатывает нажатие на опцию
        // Меняем видимость поповера, если нажали на Enter в поле
        if (code === "Enter" && !(isSupportContentEmpty && popoverState.visible)) {
          popoverState.toggle();
        }
      }
    };

    const handleFieldChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
      const { value: updatedValue } = target;
      setSelectedValue("");
      onChange(updatedValue);
    };

    const handleOptionClick = (event: MouseEvent<HTMLLIElement>) => {
      const { textContent } = event.currentTarget;
      const optionValue = event.currentTarget.getAttribute("value");
      const returnedValue = optionValue ?? textContent;

      onSelect(returnedValue ?? "");
      setSelectedValue(returnedValue ?? "");
      // Дожидаемся срабатывания всех эффектов и закрываем поповер
      setTimeout(popoverState.hide);
    };

    const handleOptionKeydown = (event: KeyboardEvent<HTMLLIElement>) => {
      // Отменяем дефолтное поведение Composite выбора опции на Space
      if (event.code === "Space") {
        event.preventDefault();
      }
      // Игнорируем выбор опции при закрытом Popover
      if (event.code === "Enter" && !popoverState.visible) {
        event.preventDefault();
      }
    };

    return (
      <Box position="relative">
        <Composite
          as={DisclosureField}
          {...composite}
          {...popoverState}
          {...props}
          id={`${id}-input`}
          ref={disclosureFieldRef}
          aria-controls={listboxId}
          aria-expanded={popoverState.visible}
          aria-haspopup="listbox"
          role="combobox"
          value={value}
          onClick={handleFieldClick}
          onChange={handleFieldChange}
          onKeyDown={handleFieldKeydown}
          focusable
        />
        <Listbox
          aria-label={popoverAriaLabel}
          aria-owns={supportId}
          id={listboxId}
          {...composite}
          {...popoverState}
          aria-activedescendant={undefined}
          onFocus={handleListboxFocus}
        >
          <ListboxContent
            {...composite}
            isVisible={popoverState.visible}
            mainContent={mainContent}
            supportContent={supportContent}
            selectedId={selectedItemId}
            onOptionClick={handleOptionClick}
            onOptionKeydown={handleOptionKeydown}
          />
        </Listbox>
        <VisuallyHidden>
          <Box aria-live="polite" role="status">
            <Box key={supportContent.length}>{comboboxHint}</Box>
          </Box>
        </VisuallyHidden>
      </Box>
    );
  }
);
